import { put, take, select, call, debug, eventChannel, EventChannel } from "../../lib";
import { LOGOUT_PENDING_STORAGE_KEY } from "../../lib/auth/logout_pending";
import {
  IDLE_SIGN_OUT_STORAGE_KEY,
  SESSION_CAP_SIGN_OUT_STORAGE_KEY,
} from "../../lib/auth/idle_sign_out";
import { SESSION_SCOPE_STORAGE_KEY, noteSharedSessionNonce } from "../../lib/auth/session_scope";
import {
  AUTH_SET,
  AUTH_REFRESHING_FAILURE,
  AUTH_REFRESH_UNAVAILABLE,
  doAUTH_REFRESHING_FAILURE,
  doSESSION_END_REASON_SET,
  SessionEndReason,
} from "../state";

// `storage` fires in every tab except the one that wrote the key, which is exactly the audience:
// the tab that wrote it has already signed itself out. Only the key being set (or re-set) counts.
// The pending-logout marker's removal is a login or a confirmed logout elsewhere, the idle signal is
// removed straight after it is sent, and `localStorage.clear()` arrives with `key: null`. The
// listener reads the event, never `localStorage` itself, so private-mode storage cannot throw here.
const createStorageKeySetChannel = (key: string) =>
  eventChannel<true>((emit) => {
    const listener = (event: StorageEvent) => {
      if (event.key === key && event.newValue !== null) emit(true);
    };
    window.addEventListener("storage", listener);
    return () => window.removeEventListener("storage", listener);
  });

export function* handleSiblingLogout(reason: SessionEndReason = SessionEndReason.SessionEnded) {
  debug("saga*handleSiblingLogout");

  // an attempt already in flight passed its marker check before the marker existed, so the
  // AUTH_SET it may still deliver would sign this tab straight back in. Let it land first.
  const isRefreshing: boolean = yield select((state: any) => state?.auth?.isRefreshing);
  if (isRefreshing) yield take([AUTH_SET, AUTH_REFRESHING_FAILURE, AUTH_REFRESH_UNAVAILABLE]);

  // no token means the tab is on the login page already, showing a reason of its own
  const accessToken: string | null = yield select((state: any) => state?.auth?.accessToken);
  if (!accessToken) return;

  // the refresh coordinator's exit for a pending logout, minus its logout request: the shared retry
  // (`retryPendingLogout`) sends a pending logout from the tab in view, and the tab that announced
  // an idle sign-out sends its own. One more from a background tab could clear the cookie of a login
  // made there.
  yield put(doSESSION_END_REASON_SET({ reason, at: Date.now() }));
  yield put(doAUTH_REFRESHING_FAILURE());
}

export function* handleSiblingLogoutSaga() {
  const channel: EventChannel<true> = yield call(
    createStorageKeySetChannel,
    LOGOUT_PENDING_STORAGE_KEY,
  );
  for (;;) {
    yield take(channel);
    yield call(handleSiblingLogout);
  }
}

// Another tab signed out for inactivity, or the user chose "Sign out" in its warning. Every tab
// shares the activity clock, so this one has been idle just as long.
export function* handleSiblingIdleSignOutSaga() {
  const channel: EventChannel<true> = yield call(
    createStorageKeySetChannel,
    IDLE_SIGN_OUT_STORAGE_KEY,
  );
  for (;;) {
    yield take(channel);
    yield call(handleSiblingLogout, SessionEndReason.Inactivity);
  }
}

// Another tab reached the session's cap. Every tab of the sign-in shares the cap, so this one's
// session ended with it.
export function* handleSiblingSessionCapSignOutSaga() {
  const channel: EventChannel<true> = yield call(
    createStorageKeySetChannel,
    SESSION_CAP_SIGN_OUT_STORAGE_KEY,
  );
  for (;;) {
    yield take(channel);
    yield call(handleSiblingLogout, SessionEndReason.SessionEnded);
  }
}

// Every change to the shared sign-in record, as the value it was changed to. `localStorage.clear()`
// arrives with `key: null` and takes the record with it.
const createSessionNonceChannel = () =>
  eventChannel<{ nonce: string | null }>((emit) => {
    const listener = (event: StorageEvent) => {
      if (event.key === SESSION_SCOPE_STORAGE_KEY) emit({ nonce: event.newValue });
      else if (event.key === null) emit({ nonce: null });
    };
    window.addEventListener("storage", listener);
    return () => window.removeEventListener("storage", listener);
  });

// Another tab signed in, or its sign-in ended. This tab's private work stops at once -- its token may
// now belong to an ended session, and the cookie to someone else -- but the tab stays signed in: its
// next refresh proves whose the cookie is, and binds again or signs out (`identity_refresh_token_submit`).
export function* handleSiblingSessionScopeSaga() {
  const channel: EventChannel<{ nonce: string | null }> = yield call(createSessionNonceChannel);
  for (;;) {
    const { nonce }: { nonce: string | null } = yield take(channel);
    noteSharedSessionNonce(nonce);
  }
}
